import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

// MUI
import { Box, Divider, IconButton, Slide, Stack, Typography } from '@mui/material';

// Style
import makeStyles from '@mui/styles/makeStyles';

// Icons
import { NavigateBefore, NavigateNext } from '@mui/icons-material';

import ItemListView from 'Components/ItemListView';
import { ApplicationIcons } from 'icons/ApplicationIcons';
import useWidth from 'services/Hooks/useWidth';
import { useFrequentlyBoughtTogethers } from 'services/Data/FrequentlyBoughtTogetherService';
import FrequentlyBoughtTogetherTypeEnum from 'enums/FrequentlyBoughtTogetherTypeEnum';
import useImage from 'services/Hooks/useImage';
import GridItem from '@jumbo/components/GridItem';
import InventoryItemDealerPortalTypeEnum from 'enums/InventoryItemDealerPortalTypeEnum';
import { usePanelHeaders } from 'services/Data/WallService';
import { CeilingHeaderEnumValue } from 'enums/CeilingHeaderEnum';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { useActiveCart, usePostCarItem, usePostCartTooling } from 'services/Data/CartService';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import GridContainer from '@jumbo/components/GridContainer';
import { useSelector } from 'react-redux';
import CartRedirectionModal from './CartRedirectionModal';
import { IsImpersonationMode } from 'config/config';

const useStyles = makeStyles(theme => ({
  customFrequentlyBoughtTogetherCard: {
    width: '25rem',
  },
}));

const FrequentlyBoughtTogether = ({ entityId, frequentlyBoughtTogetherType, cardsPerPage = 4 }) => {
  const classes = useStyles();

  const { t } = useTranslation();
  const { nameReference } = useLanguageReference();

  const accountReducer = useSelector(state => state.AccountReducer);
  const dealerId = accountReducer?.profile?.dealer_Id;

  const history = useHistory();

  const width = useWidth();

  const { data: panelHeaders } = usePanelHeaders();
  const { data: cart } = useActiveCart(accountReducer.isLoggedIn, false, IsImpersonationMode ? dealerId : null);

  const { mutate: addCartTooling } = usePostCartTooling(accountReducer.isDemo);
  const { mutate: addCartItem } = usePostCarItem(accountReducer.isDemo);

  const {
    data: frequentlyBoughtTogethers,
    isFetching: gettingCurrentFrequentlyBoughtTogether,
  } = useFrequentlyBoughtTogethers(entityId, frequentlyBoughtTogetherType, true);
  const { getToolingImage, getInventoryItemImage, getPanelTypeImage } = useImage();

  const [cards, setCards] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentCardsPerPage, setCurrentCardsPerPage] = useState(cardsPerPage);
  const [slideDirection, setSlideDirection] = useState('left');
  const [showRedirectionModal, setShowRedirectionModal] = useState(false);

  const frequentlyBoughtTogheterContainerRef = useRef();

  const handleNextPage = useCallback(() => {
    setSlideDirection('left');
    setCurrentPage(currentPage + 1);
  }, [currentPage]);

  const handlePreviousPage = useCallback(() => {
    setSlideDirection('right');
    setCurrentPage(currentPage - 1);
  }, [currentPage]);

  useEffect(() => {
    if (frequentlyBoughtTogheterContainerRef?.current && currentPage > 0) {
      setTimeout(() => {
        frequentlyBoughtTogheterContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
      }, 1000);
    }
  }, [currentPage]);

  useEffect(() => {
    if (!frequentlyBoughtTogethers) return;

    const filtered = frequentlyBoughtTogethers.filter(item => {
      if (item.promotionPrice > 0 && item.promotionQuantity <= 0) {
        return false;
      }
      return true;
    });

    setCards(filtered);
  }, [frequentlyBoughtTogethers]);

  const handleAddToCart = useCallback(
    frequentlyBoughtTogether => {
      if (frequentlyBoughtTogether.frequentlyBoughtTogetherTypeEnum === FrequentlyBoughtTogetherTypeEnum.Item) {
        const cartItem = {
          id: uuidv4(),
          inventoryItem_Id: frequentlyBoughtTogether.id,
          quantity: 1,
          unitPrice: frequentlyBoughtTogether.price,
          currencyName: frequentlyBoughtTogether.currencyName,
          cart_Id: cart?.id,
          unitPricePromotion: frequentlyBoughtTogether.promotionPrice,
          promotionQuantity: frequentlyBoughtTogether.promotionQuantity,
        };
        addCartItem(cartItem);
        setShowRedirectionModal(true);
      } else if (frequentlyBoughtTogether.frequentlyBoughtTogetherTypeEnum === FrequentlyBoughtTogetherTypeEnum.Tooling) {
        const cartItem = {
          id: uuidv4(),
          dateCreated: new Date(),
          quantity: 1,
          tooling_Id: frequentlyBoughtTogether.id,
          subTotal: frequentlyBoughtTogether.price,
          price: frequentlyBoughtTogether.price,

          cart_Id: cart?.id,
        };

        addCartTooling(cartItem);
        setShowRedirectionModal(true);
      }
    },
    [addCartItem, addCartTooling, cart?.id],
  );

  const handleOnClickCard = useCallback(
    frequentlyBoughtTogether => {
      if (
        frequentlyBoughtTogether.frequentlyBoughtTogetherTypeEnum === FrequentlyBoughtTogetherTypeEnum.Item ||
        frequentlyBoughtTogether.frequentlyBoughtTogetherTypeEnum === FrequentlyBoughtTogetherTypeEnum.Tooling
      ) {
        var associatedCategory = InventoryItemDealerPortalTypeEnum.find(
          c => c.value === frequentlyBoughtTogether.dealerPortalTypeEnum,
        );

        if (associatedCategory.parentCategory) {
          var parentAssociatedCategory = InventoryItemDealerPortalTypeEnum.find(
            c => c.value === associatedCategory.parentCategory,
          );

          if (parentAssociatedCategory.parentCategory) {
            history.push(
              `/dealer/${parentAssociatedCategory.route}/${associatedCategory.route}/${frequentlyBoughtTogether.id}`,
            );
          } else {
            history.push(`/dealer/${associatedCategory.route}/${frequentlyBoughtTogether.id}`);
          }
        } else {
          history.push(`/dealer/${associatedCategory.route}/${frequentlyBoughtTogether.id}`);
        }
      }

      if (frequentlyBoughtTogether.frequentlyBoughtTogetherTypeEnum === FrequentlyBoughtTogetherTypeEnum.Wall) {
        history.push(`/dealer/walls/${frequentlyBoughtTogether.id}`);
      }
    },
    [history],
  );

  useEffect(() => {
    if (width === 'xs') {
      setCurrentCardsPerPage(1);
    } else if (width === 'sm') {
      setCurrentCardsPerPage(1);
    } else if (width === 'md' && currentCardsPerPage > 2) {
      setCurrentCardsPerPage(2);
    }
  }, [currentCardsPerPage, width]);

  if (cards?.length === 0) return <Box></Box>;

  return (
    <>
      <Box>
        <Divider sx={{ mb: 4, mt: 2 }} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          alignContent: 'center',
          justifyContent: 'center',
          minHeight: '15rem',
        }}>
        {cards?.length > 0 && (
          <Typography variant="h1" sx={{ mb: 5 }}>
            {t('frequently_bought_together')}
          </Typography>
        )}

        <CartRedirectionModal
          show={showRedirectionModal}
          onClose={() => setShowRedirectionModal(false)}
          redirectLink={'/dealer/cart'}
          title={t('itemAddedToCart')}
          message={t('itemAddedToCartText')}
        />

        <SkeletonWrapper
          fullWidth
          isLoading={gettingCurrentFrequentlyBoughtTogether}
          skeletonProps={{ height: '35rem' }}
          component={
            <GridContainer
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                alignContent: 'center',
                justifyContent: 'center',
                minHeight: '35rem',
              }}
            />
          }>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              alignContent: 'center',
              justifyContent: 'center',
              minHeight: '15rem',
              width: '100%',
            }}>
            <IconButton
              onClick={handlePreviousPage}
              sx={{
                margin: '5',
              }}
              disabled={currentPage === 0}>
              <NavigateBefore />
            </IconButton>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                alignContent: 'center',
                justifyContent: 'center',
                minHeight: '15rem',
                width: '100%',
              }}>
              {cards?.map((card, index) => (
                <Box
                  key={`card-${index}`}
                  ref={frequentlyBoughtTogheterContainerRef}
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: currentPage === index ? 'block' : 'none',
                  }}>
                  <Slide direction={slideDirection} in={currentPage === index}>
                    <Stack spacing={2} direction="row" alignContent="center" justifyContent="space-around">
                      {cards.slice(index * currentCardsPerPage, index * currentCardsPerPage + currentCardsPerPage).map(c => {
                        const existingCartItem = cart?.cartItems?.find(ci => ci.inventoryItem_Id === c.id);
                        const quantityInCart = existingCartItem?.quantity ?? 0;
                        const isPromoLimitReached = c.promotionPrice > 0 && quantityInCart >= c.promotionQuantity;

                        return (
                          <GridItem className={classes.customFrequentlyBoughtTogetherCard}>
                            <ItemListView
                              key={c.id}
                              onClick={() => handleOnClickCard(c)}
                              view={'card'}
                              title={`${c.name}${
                                c.frequentlyBoughtTogetherTypeEnum === FrequentlyBoughtTogetherTypeEnum.Wall
                                  ? ` ${
                                      panelHeaders.find(ph => ph.id === CeilingHeaderEnumValue.HighCeiling)[nameReference]
                                    }`
                                  : ''
                              }`}
                              description={
                                c.frequentlyBoughtTogetherTypeEnum === FrequentlyBoughtTogetherTypeEnum.Wall
                                  ? t('startAt')
                                  : c.shortDescription
                              }
                              identification={c.sku}
                              quantityPerPackage={c.quantityPerPackage}
                              imgSrc={
                                c.images?.length > 0
                                  ? c.frequentlyBoughtTogetherTypeEnum === FrequentlyBoughtTogetherTypeEnum.Wall
                                    ? getPanelTypeImage(
                                        c?.id,
                                        c?.images.find(i => i.dealerPortalPrimary)
                                          ? c?.images.find(i => i.dealerPortalPrimary).id
                                          : c?.images[0].id,
                                      )
                                    : c.frequentlyBoughtTogetherTypeEnum === FrequentlyBoughtTogetherTypeEnum.Tooling
                                    ? getToolingImage(
                                        c?.id,
                                        c?.images.find(i => i.dealerPortalPrimary)
                                          ? c?.images.find(i => i.dealerPortalPrimary).id
                                          : c?.images[0].id,
                                      )
                                    : c.frequentlyBoughtTogetherTypeEnum === FrequentlyBoughtTogetherTypeEnum.Item
                                    ? getInventoryItemImage(
                                        c?.id,
                                        c?.images.find(i => i.dealerPortalPrimary)
                                          ? c?.images.find(i => i.dealerPortalPrimary).id
                                          : c?.images[0].id,
                                      )
                                    : ''
                                  : '/images/brp-logo.png'
                              }
                              price={c.price}
                              promotionPrice={c.promotionPrice}
                              promotionQuantity={c.promotionQuantity}
                              currency={c.currencyName}
                              actions={
                                c.hasAdditionalToolings ||
                                c.frequentlyBoughtTogetherTypeEnum === FrequentlyBoughtTogetherTypeEnum.Wall
                                  ? [
                                      {
                                        icon: ApplicationIcons.Favorite,
                                        color: 'secondary',
                                        text: t('setUp'),
                                        onClick: () => handleOnClickCard(c),
                                      },
                                    ]
                                  : [
                                      {
                                        icon: ApplicationIcons.Favorite,
                                        color: 'secondary',
                                        text: t('addToCart'),
                                        onClick: () => handleAddToCart(c),
                                        disabled: isPromoLimitReached,
                                      },
                                    ]
                              }></ItemListView>
                          </GridItem>
                        );
                      })}
                    </Stack>
                  </Slide>
                </Box>
              ))}
            </Box>

            <IconButton
              onClick={handleNextPage}
              sx={{
                margin: '5',
              }}
              disabled={currentPage >= Math.ceil((cards?.length || 0) / currentCardsPerPage) - 1}>
              <NavigateNext />
            </IconButton>
          </Box>
        </SkeletonWrapper>
      </Box>
    </>
  );
};
export default FrequentlyBoughtTogether;
